import { track } from '@eventbrite/datalayer-library';
import * as Sentry from '@sentry/browser';
import { Dispatch } from 'redux';
import { setScrollCalendarData } from '../actions/infiniteCalendarSeries';
import { TlzEnvironment } from '../api/constants';
import { getEventCalendarDates } from '../api/eventDates';
import { getMonthAndYearFromDate } from '../components/InfiniteCalendarSeries/utils';
import { mapEventDatesToScrollCalendarData } from '../components/TimedEntrySeries/CalendarContainer/utils';
import { HeapEventName } from '../constants/heap/events';
import { getBffRequestBaseParams } from '../selectors/bff';
import { getEventStartDate } from '../selectors/event';
import {
    getParentEventInfo,
    getScrollCalendarData,
    getTimezone,
} from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { CheckoutState } from '../state';
import {
    DateApiResponse,
    type ScrollCalendarData,
} from '../state/infiniteCalendarSeries';
import { getCurrentOrFutureMonth, isFirstTwoMonths } from '../utils/timedEntry';

type EventDateParams = {
    eventId: string;
    env: TlzEnvironment;
    month?: number;
    year?: number;
};

type CalendarTypeUpdate = Pick<EventDateParams, 'month' | 'year'> & {
    state: CheckoutState;
    calendarData: DateApiResponse;
    dispatch: Dispatch<CheckoutState>;
};

const shouldSkipLoading = (
    previousCalendarData: ScrollCalendarData,
    month?: number,
    year?: number,
): boolean => {
    if (month == null || year == null) {
        return false;
    }
    return Boolean(previousCalendarData?.years?.[year]?.months?.[month]);
};

export const _initializeScrollCalendarData = ({
    previousScrollCalendarData,
    month,
    year,
    dispatch,
}: {
    previousScrollCalendarData: ScrollCalendarData;
    month: number;
    year: number;
    dispatch: Dispatch<CheckoutState>;
}) => {
    const calendarData = _getLoadingCalendarData(
        previousScrollCalendarData,
        month,
        year,
    );
    dispatch(setScrollCalendarData(calendarData));
};

export const _getLoadingCalendarData = (
    previousCalendarData: ScrollCalendarData,
    month: number,
    year: number,
): ScrollCalendarData => {
    const calendarData = previousCalendarData || { years: {} };
    calendarData.years ??= {};
    calendarData.years[year] ??= { months: {} };
    calendarData.years[year].months[month] ??= {
        isLoading: true,
        dates: {},
    };
    return calendarData;
};

const updateScrollCalendar = ({
    calendarData,
    state,
    month,
    year,
    dispatch,
}: CalendarTypeUpdate) => {
    const { month: _month, year: _year } =
        month == null && year == null
            ? getMonthAndYearFromDate(calendarData.eventDates[0].date)
            : { month, year };

    const previousScrollCalendarData = getScrollCalendarData(state) || {};
    const timezone = getTimezone(state);

    // About `_month as number, _year as number` 👇:
    // When month and year are not provided, it is the first api call to `/dates` API which will return eventDates and calendarType.
    // In that case, month and year will be taken from the first element of the `response.eventDates` array
    // On subsequent calls to `/dates` function, month and year will be provided by `ScrollableCalendar` (via `onMonthLoad` prop)
    // So _month, _year will always have values.
    const scrollCalendarData = mapEventDatesToScrollCalendarData({
        eventDates: calendarData.eventDates,
        previousScrollCalendarData,
        month: _month as number,
        year: _year as number,
        timezone,
    });
    dispatch(setScrollCalendarData(scrollCalendarData));
};

export const setScrollCalendar = ({
    calendarData,
    state,
    dispatch,
    month,
    year,
}: CalendarTypeUpdate): void => {
    updateScrollCalendar({
        calendarData,
        state,
        month,
        year,
        dispatch,
    });

    const eventStartDate = getEventStartDate(state);
    const nextAvailableDate = getCurrentOrFutureMonth(eventStartDate.utc);

    const shouldScrollEventBeTracked =
        month && year && !isFirstTwoMonths(month, year, nextAvailableDate);

    if (shouldScrollEventBeTracked) {
        track({
            eventName: HeapEventName.timedEntryDateAccordionScroll,
            eventData: {
                isSeriesInfiniteCalendar: true,
            },
        });
    }
};

const _fetchCalendarData = async ({
    month,
    year,
    eventId,
    env,
}: Required<EventDateParams>): Promise<DateApiResponse | null> => {
    const params: EventDateParams = {
        eventId,
        env,
        // Note about `params.month = month + 1` 👇:
        // The month argument will have zero-indexed month, meaning January is 0.
        // The `/dates` API expects a one-indexed month, meaning January is 1.
        // For December 2024, the month and year arguments will be 11 and 2024, respectively, and these will be passed to the API as 12 and 2024.
        // For January 2025, the month and year arguments will be 0 and 2025, but they will be passed as 1 and 2025, respectively.
        month: month + 1,
        year,
    };

    return await getEventCalendarDates(params);
};

export const loadDatesForMonth =
    (month: number, year: number) =>
    async (
        dispatch: Dispatch<CheckoutState>,
        getState: () => CheckoutState,
    ) => {
        const state = getState();
        const previousScrollCalendarData = getScrollCalendarData(state) || {};
        if (shouldSkipLoading(previousScrollCalendarData, month, year)) {
            return;
        }
        _initializeScrollCalendarData({
            previousScrollCalendarData,
            month,
            year,
            dispatch,
        });

        try {
            const { id: eventId } = getParentEventInfo(state);
            const { env } = getBffRequestBaseParams(state);

            const calendarData = await _fetchCalendarData({
                month,
                year,
                eventId,
                env,
            });

            if (!calendarData) {
                throw new Error('No calendar data available from API');
            }

            setScrollCalendar({
                calendarData,
                state,
                dispatch,
                month,
                year,
            });
        } catch (error) {
            Sentry.captureException(
                `Error fetching calendar data for month: ${month}, year: ${year}`,
                {
                    extra: {
                        error,
                    },
                },
            );
        }
    };
